<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { styleZoom } from "@/common/utils/style";
import { fileAvatarFileShow } from "@/common/api/rest/file/avatar";

const props = defineProps<{
  block: any;
  selectedBlock: string | null;
  zoom: number;
}>();

/** Cache de imagens legadas (value = id → data URL) */
const images = ref<{ [key: string]: string }>({});

const columns = computed(() =>
  (props.block?.content?.columns ?? []).filter((col: any) => !!col.visible),
);

const rows = computed(() =>
  (props.block?.content?.rows ?? [])
    .filter((row: any) => !!row.visible)
    .filter((row: any) =>
      (row.items ?? []).some((item: any) => {
        const col = columns.value.find((c: any) => c.id === item.col);
        return col && col.visible;
      }),
    ),
);

/**
 * Resolve a URL final para exibir uma imagem.
 * - Se `value` já for uma data URL (o editor já resolveu), usa direto.
 * - Se for um objeto com { file, mime_type } → monta data URL.
 * - Se for um objeto com { base64 } → usa direto.
 * - Caso contrário (legado), trata como ID e busca no cache `images`.
 */
function resolveImageSrc(value: any): string {
  if (!value) return "";

  if (typeof value === "string") {
    if (value.startsWith("data:")) return value;
    return images.value[value] ?? "";
  }

  if (value.file) {
    return `data:${value.mime_type ?? "image/png"};base64,${value.file}`;
  }

  if (value.base64) return value.base64;

  return "";
}

/**
 * Busca via API apenas os valores que:
 *  - são strings (IDs legados)
 *  - NÃO são data URLs
 *  - ainda não estão no cache
 */
async function getImages() {
  const idsToFetch = new Set<string>();

  (props.block?.content?.rows ?? []).forEach((row: any) => {
    (row?.items ?? []).forEach((item: any) => {
      if (!item?.is_image) return;

      const value = item.value;
      if (typeof value !== "string") return;
      if (!value) return;
      if (value.startsWith("data:")) return; // já resolvido pelo editor
      if (images.value[value]) return; // já em cache

      idsToFetch.add(value);
    });
  });

  const ids = Array.from(idsToFetch);
  if (ids.length === 0) return;

  const promises = ids.map((id) =>
    fileAvatarFileShow(props.block.account_id, id),
  );

  const response = await Promise.all(promises);

  response.forEach((res, index) => {
    // guarda contra res.data nulo (id inválido)
    if (res?.success && res.data?.base64) {
      images.value[ids[index]] = res.data.base64;
    }
  });
}

watch(
  () => props.block,
  async () => {
    if (!props.block) return;
    await getImages();
  },
  { deep: true, immediate: true },
);

onMounted(async () => {
  if (!props.block) return;
  await getImages();
});
</script>

<template>
  <div
    class="table-block"
    :class="{ selected: !!selectedBlock }"
    :style="styleZoom(block.style, zoom)"
  >
    <template v-if="block.content?.title">
      <div class="table-title">
        {{ block.content?.title }}
      </div>
    </template>
    <table class="data-table">
      <thead>
        <tr>
          <template v-for="col in columns" :key="col.id">
            <th>{{ col.label }}</th>
          </template>
        </tr>
      </thead>

      <tbody>
        <template v-for="row in rows" :key="row.id">
          <tr>
            <template v-for="item in row.items" :key="item.id">
              <template v-if="columns.some((c: any) => c.id === item.col)">
                <td>
                  <template v-if="item.is_image">
                    <img
                      v-if="resolveImageSrc(item.value)"
                      class="cell-image"
                      :src="resolveImageSrc(item.value)"
                    />
                    <template v-else> - </template>
                  </template>
                  <template v-else>
                    <template v-if="item.value !== null && item.value !== ''">
                      {{ item.value }}
                    </template>
                    <template v-else> - </template>
                  </template>
                </td>
              </template>
            </template>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.table-block {
  overflow-x: auto;

  .table-title {
    padding-inline: 0.875em;
    padding-block: 0.3em;
    font-size: 1em;
    font-weight: bold;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-bottom: 0;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #dee2e6;

    th,
    td {
      border: 1px solid #dee2e6;
      padding-inline: 0.875em;
      padding-block: 0.5em;
    }

    th {
      background: #f8f9fa;
      font-weight: bold;
      font-size: 0.875em;
      text-align: center;
    }

    td {
      background: #fff;
      font-weight: normal;
      font-size: 0.875em;
      text-align: center;

      .cell-image {
        display: inline-block;
        max-width: 150px;
        height: auto;
      }
    }
  }
}
</style>
